import React, { FC, useCallback, useMemo, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import {
  Tabs,
  TabType,
  CardVariant,
  CardSize,
  Button,
  ContentCard,
  Search,
} from "components/ui";
import { UrlSearchParams, DiscoverTabs } from "types/enums";
import { trackEvent } from "utility/mixpanel";
import {
  LEARNER_SCREEN_RECORDINGS,
  LEARNER_SCREEN_SESSIONS_WEB,
  LEARNER_SCREEN_ARTICLES,
  QUERY_HIGHLIGHTED_RECORDINGS,
} from "utility/graphQl/query";
import appInfo from "utility/app-info";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";
import { extractItem } from "components/ui/ContentCard/utils";
import ContentCardGrid from "../../components/content-card-grid";
import ScheduledList from "../../components/scheduled-list";
import CarouselList from "../../components/carousel-list";
import { ContentListType } from "../home/types";
import { PageContainer, PageContent } from "../../components/page-container";
import { ReactComponent as SessionIcon } from "Assets/icon/icon-clip.svg";
import { ReactComponent as CalendarIcon } from "Assets/icon/icon-calendar.svg";
import { ReactComponent as ArticleIcon } from "Assets/icon/icon-open-book.svg";
import { usePaginatedQuery } from "../../hooks/use-paginated-query";
import { Carousel } from "../../components/carousel";
import { LearnerRecording } from "../../api/types";
import styles from "./discover.module.scss";

import { ContentFilterTag } from "../../components/discover/types";
import {
  extractFilters,
  extractSelectedFilters,
  processSearchParams,
  hasActiveFiltersUtil,
  formatAppliedFilters,
  extractFiltersFromSearchParams,
} from "../../components/discover/utils";
import { TabContent } from "../../components/discover/TabContent";

const defaultFilters = {};
const DEFAULT_PAGE_SIZE = 18;
const CAROUSEL_OFFSET_OVERRIDE = 616;
const HERO_CAROUSEL_ITEMS = 10;

const Discover: FC = () => {
  const { t } = useTranslation(["discover", "common"]);
  const [sidebarFilters, setSidebarFilters] = useState<ContentFilterTag[]>([]);
  const [topicsFilter, setTopicsFilter] = useState<ContentFilterTag | null>(
    null
  );
  const [showFilters, setShowFilters] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const { urlFilters, processedFilters } = useMemo(
    () => processSearchParams(searchParams, setSearchParams),
    [searchParams, setSearchParams]
  );

  const searchQuery = searchParams.get(UrlSearchParams.Search) || "";
  const activeTab =
    (searchParams.get(UrlSearchParams.Tab) as DiscoverTabs) ||
    DiscoverTabs.SESSIONS;

  const getRecordingsFromData = (data: any) => {
    return data?.learner?.screens?.recordings?.filteredContentList?.items || [];
  };

  const updateRecordingsCache = (prev: any, newItems: any[]) => {
    const prevItems =
      prev?.learner?.screens?.recordings?.filteredContentList?.items || [];
    const combinedItems = [...prevItems, ...newItems];

    return {
      ...prev,
      learner: {
        ...prev.learner,
        screens: {
          ...prev.learner.screens,
          recordings: {
            ...prev.learner.screens.recordings,
            filteredContentList: {
              ...prev.learner.screens.recordings.filteredContentList,
              items: combinedItems,
            },
          },
        },
      },
    };
  };

  const {
    loading: heroCarouselLoading,
    error: heroCarouselError,
    data: heroCarouselData,
  } = useQuery(QUERY_HIGHLIGHTED_RECORDINGS, {
    fetchPolicy: "cache-and-network",
    onError(error) {
      logError(
        "Error while fetching hero carousel data for discover page",
        error
      );
    },
    variables: {
      appInfo,
    },
  });

  const {
    data: recordingsData,
    loading: recordingsLoading,
    error: recordingsError,
    hasMore: hasMoreRecordings,
    isLoadingMore,
    loadMore,
  } = usePaginatedQuery(LEARNER_SCREEN_RECORDINGS, {
    fetchPolicy: "cache-and-network",
    skip: activeTab !== DiscoverTabs.SESSIONS,
    onError(error) {
      logError("Error while fetching recordings for discover page", error);
    },
    variables: {
      appInfo,
      filters: processedFilters,
      searchQuery:
        searchQuery && searchQuery.length > 0 ? searchQuery : undefined,
    },
    extractItems: getRecordingsFromData,
    updateCacheWithNewItems: updateRecordingsCache,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const {
    loading: liveSessionsLoading,
    error: liveSessionsError,
    data: liveSessionsData,
  } = useQuery(LEARNER_SCREEN_SESSIONS_WEB, {
    fetchPolicy: "cache-and-network",
    skip: activeTab !== DiscoverTabs.LIVE_SCHEDULE,
    onError(error) {
      logError("Error while fetching sessions for discover page", error);
    },
    variables: {
      filters: processedFilters,
      appInfo,
      searchQuery:
        searchQuery && searchQuery.length > 0 ? searchQuery : undefined,
    },
  });

  const {
    loading: articlesLoading,
    error: articlesError,
    data: articlesData,
  } = useQuery(LEARNER_SCREEN_ARTICLES, {
    fetchPolicy: "cache-and-network",
    skip: activeTab !== DiscoverTabs.QUICK_READS,
    onError(error) {
      logError("Error while fetching articles for discover page", error);
    },
    variables: {
      filters: defaultFilters,
      appInfo,
    },
  });

  const handleSearchSubmit = (query: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (query && query.length > 0) {
      newSearchParams.set(UrlSearchParams.Search, query);
    } else {
      newSearchParams.delete(UrlSearchParams.Search);
    }
    setSearchParams(newSearchParams);
  };

  const trackFiltersApplied = (params: Record<string, string[]>) => {
    const appliedCategories = Object.keys(params)
      .filter((key) => {
        const values = params[key];
        return values.some((value) => value !== "all");
      })
      .sort();
    const appliedOptions = formatAppliedFilters(params);
    trackEvent("Filters Applied", {
      "Applied Categories": appliedCategories,
      "Applied Options": appliedOptions,
      "Search Term": searchQuery,
      "Content Type": activeTab,
    });
  };

  const clearFilters = (keys: string[]) => {
    const appliedOptions = formatAppliedFilters(urlFilters);
    trackEvent("Filters Cleared", {
      "Search Term": searchQuery,
      "Content Type": activeTab,
      "Previous Filters": appliedOptions,
    });

    const newSearchParams = new URLSearchParams(searchParams);

    keys.forEach((key) => {
      newSearchParams.delete(key);
    });

    setSearchParams(newSearchParams);
  };

  const handleFilterChange = (filterGroupName: string, filterValue: string) => {
    const ALL_VALUE = "all";
    const next = new URLSearchParams(searchParams);
    const existingRaw = next.get(filterGroupName);

    if (!existingRaw) {
      next.set(filterGroupName, filterValue);
      setSearchParams(next);
      trackFiltersApplied(extractFiltersFromSearchParams(next));
      return;
    }

    if (filterValue === ALL_VALUE) {
      next.set(filterGroupName, ALL_VALUE);
      setSearchParams(next);
      trackFiltersApplied(extractFiltersFromSearchParams(next));
      return;
    }

    let values = existingRaw.split(",");
    if (values.includes(ALL_VALUE)) {
      values = values.filter((v) => v !== ALL_VALUE);
    }

    const alreadySelected = values.includes(filterValue);
    if (alreadySelected) {
      const remaining = values.filter((v) => v !== filterValue);
      if (remaining.length === 0) {
        next.delete(filterGroupName);
      } else {
        next.set(filterGroupName, remaining.join(","));
      }
    } else {
      values.push(filterValue);
      next.set(filterGroupName, values.join(","));
    }
    setSearchParams(next);
    trackFiltersApplied(extractFiltersFromSearchParams(next));
  };

  const handleTabChange = (newTab: string) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set(UrlSearchParams.Tab, newTab);

    setSearchParams(newSearchParams);
  };

  const extractAndSetFilters = useCallback(
    (filtersFromQuery: any) => {
      if (filtersFromQuery) {
        const { sidebarFilterTags, topics } = extractFilters(
          filtersFromQuery,
          t("discover:filters.all")
        );
        setSidebarFilters(sidebarFilterTags);
        setTopicsFilter(topics);
      }
    },
    [t]
  );

  useEffect(() => {
    if (activeTab === DiscoverTabs.SESSIONS) {
      const filtersFromQuery =
        recordingsData?.learner?.screens?.recordings?.filteredContentList
          ?.filters;

      extractAndSetFilters(filtersFromQuery);
    } else if (activeTab === DiscoverTabs.LIVE_SCHEDULE) {
      const filtersFromQuery =
        liveSessionsData?.learnerScreenSessions?.contentList?.filters;

      extractAndSetFilters(filtersFromQuery);
    } else {
      setSidebarFilters([]);
      setTopicsFilter(null);
    }
  }, [activeTab, recordingsData, liveSessionsData, extractAndSetFilters]);

  const { selectedTopics, selectedSidebarFilterTags } = useMemo(
    () => extractSelectedFilters(urlFilters),
    [urlFilters]
  );

  const hasActiveFilters = useMemo(
    () => hasActiveFiltersUtil(urlFilters),
    [urlFilters]
  );

  const getCurrentTabData = () => {
    switch (activeTab) {
      case DiscoverTabs.SESSIONS:
        return {
          items:
            recordingsData?.learner?.screens?.recordings?.filteredContentList
              ?.items || [],
          loading: recordingsLoading,
          error: recordingsError,
        };
      case DiscoverTabs.LIVE_SCHEDULE:
        return {
          items:
            liveSessionsData?.learnerScreenSessions?.contentList?.items || [],
          loading: liveSessionsLoading,
          error: liveSessionsError,
        };
      case DiscoverTabs.QUICK_READS:
        return {
          items:
            articlesData?.learner?.screens?.articles?.filteredContentList
              ?.items || [],
          loading: articlesLoading,
          error: articlesError,
        };
      default:
        return {
          items: [],
          loading: false,
          error: null,
        };
    }
  };

  const currentTabData = getCurrentTabData();

  const heroCarouselItems = useMemo(() => {
    if (!heroCarouselData?.learnerContentList || heroCarouselLoading) {
      return [];
    }

    return heroCarouselData.learnerContentList.items
      .slice(0, HERO_CAROUSEL_ITEMS)
      .map((item: LearnerRecording, index: number) => {
        const cardItem = extractItem(item);
        return (
          <ContentCard
            key={cardItem.id}
            item={cardItem}
            variant={CardVariant.IMAGE}
            size={CardSize.LARGE}
            listPosition={index + 1}
            listName={ContentListType.RecentlyAdded}
          />
        );
      });
  }, [heroCarouselData, heroCarouselLoading]);

  const onSearchSubmit = (query: string) => {
    const eventName =
      query === "" ? "Discover Search Cleared" : "Discover Search Submitted";
    trackEvent(eventName, {
      "Current Tab": activeTab,
      "Search Query": query,
    });

    handleSearchSubmit && handleSearchSubmit(query);
  };

  const tabs: TabType[] = [
    {
      key: DiscoverTabs.SESSIONS,
      label: t("discover:tabs.sessions"),
      icon: SessionIcon,
      content: (
        <TabContent
          showFilters={showFilters}
          urlFilters={urlFilters}
          handleFilterToggle={() => setShowFilters(!showFilters)}
          topicsFilter={topicsFilter}
          selectedTopics={selectedTopics}
          handleFilterChange={handleFilterChange}
          activeTab={activeTab}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
          sidebarFilters={sidebarFilters}
          selectedSidebarFilterTags={selectedSidebarFilterTags}
        >
          <div className={styles.sessionCards}>
            <ContentCardGrid
              items={currentTabData.items}
              listName={ContentListType.Sessions}
              loading={currentTabData.loading}
              error={
                !!currentTabData.error && currentTabData.items.length === 0
              }
              variant={CardVariant.IMAGE}
              size={CardSize.REGULAR}
              hasActionButtons
            />
            {hasMoreRecordings && !currentTabData.loading && (
              <Button
                variant="enclosed-primary"
                onClick={loadMore}
                loading={isLoadingMore}
                disabled={currentTabData.items.length === 0 || isLoadingMore}
              >
                {t("common:action.showMore")}
              </Button>
            )}
          </div>
        </TabContent>
      ),
    },
    {
      key: DiscoverTabs.LIVE_SCHEDULE,
      label: t("discover:tabs.live"),
      icon: CalendarIcon,
      content: (
        <TabContent
          showFilters={showFilters}
          urlFilters={urlFilters}
          handleFilterToggle={() => setShowFilters(!showFilters)}
          topicsFilter={topicsFilter}
          selectedTopics={selectedTopics}
          handleFilterChange={handleFilterChange}
          activeTab={activeTab}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
          sidebarFilters={sidebarFilters}
          selectedSidebarFilterTags={selectedSidebarFilterTags}
        >
          <ScheduledList
            items={currentTabData.items}
            listName={ContentListType.LiveSchedule}
            loading={
              currentTabData.loading && currentTabData.items.length === 0
            }
            error={!!currentTabData.error && currentTabData.items.length === 0}
          />
        </TabContent>
      ),
    },
    {
      key: DiscoverTabs.QUICK_READS,
      label: t("discover:tabs.articles"),
      icon: ArticleIcon,
      content: (
        <CarouselList
          items={currentTabData.items}
          listName={ContentListType.QuickReads}
          loading={currentTabData.loading && currentTabData.items.length === 0}
          error={!!currentTabData.error && currentTabData.items.length === 0}
          variant={CardVariant.IMAGE}
          size={CardSize.TALL}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      <PageContent maxWidth="lg">
        <div className={styles.discoverContainer}>
          <Carousel
            title={
              <h2 className={styles.carouselTitle}>
                {t("discover:sessions.rapidResets")}
              </h2>
            }
            items={heroCarouselItems}
            offsetOverride={CAROUSEL_OFFSET_OVERRIDE}
            loading={heroCarouselLoading}
            error={!!heroCarouselError}
            listName={ContentListType.HeroCarousel}
          />
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            className={styles.discoverTabs}
            rightElement={
              activeTab === DiscoverTabs.SESSIONS ||
              activeTab === DiscoverTabs.LIVE_SCHEDULE ? (
                <Search
                  key={searchQuery || "empty"}
                  searchQuery={searchQuery}
                  onSubmitSearchQuery={onSearchSubmit}
                  showClearButton={!!searchQuery}
                  onClearSearch={() => onSearchSubmit("")}
                />
              ) : undefined
            }
          />
        </div>
      </PageContent>
    </PageContainer>
  );
};

export default Discover;
